import { useEffect } from 'react'
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome'
import { faGift, faSpinner, faInfoCircle } from '@fortawesome/free-solid-svg-icons'
import { useAutoApplyDiscounts } from '../../hooks/useCoupons'
import { makeNumbersFarsi } from '../../utils/farsi'
import { addThousandsSeparator } from '../../utils/format'

interface AutoApplyDiscountsProps {
  orderSubtotal: number
  customerId?: string
}

export const AutoApplyDiscounts = ({
  orderSubtotal,
  customerId,
}: AutoApplyDiscountsProps) => {
  const { autoApplyDiscounts, isLoading, error, fetchAutoApplyDiscounts } =
    useAutoApplyDiscounts(orderSubtotal, customerId)

  useEffect(() => {
    if (orderSubtotal > 0) {
      fetchAutoApplyDiscounts()
    }
  }, [orderSubtotal, customerId])

  if (isLoading) {
    return (
      <div className='border rounded-md p-4 my-4 flex items-center justify-center' style={{ backgroundColor: 'var(--card-bg)', borderColor: 'var(--border-color)' }}>
        <FontAwesomeIcon icon={faSpinner} spin className='ml-2' />
        <span>در حال بررسی تخفیف‌های قابل اعمال...</span>
      </div>
    )
  }

  if (error) {
    return null // Silently fail - don't show auto-apply errors to users
  }

  if (autoApplyDiscounts.length === 0) {
    return null // Don't show anything if no auto-apply discounts
  }

  const formatPrice = (value: number) => {
    const priceStr = value.toString().substring(0, value.toString().length - 1)
    return makeNumbersFarsi(addThousandsSeparator(priceStr))
  }

  return (
    <div
      className='border rounded-md p-4 my-4'
      style={{
        backgroundColor: 'var(--bg-secondary)',
        borderColor: 'var(--accent-primary)',
        borderWidth: '2px',
      }}
    >
      <div className='flex items-center mb-3'>
        <FontAwesomeIcon
          icon={faGift}
          className='ml-2 text-xl'
          style={{ color: 'var(--accent-primary)' }}
        />
        <h3 className='text-lg font-semibold' style={{ color: 'var(--accent-primary)' }}>
          تخفیف‌های ویژه شما
        </h3>
      </div>

      <div className='space-y-3'>
        {autoApplyDiscounts.map((discount) => {
          const isEligible = !discount.minOrderAmount || orderSubtotal >= discount.minOrderAmount
          
          return (
            <div
              key={discount.id}
              className={`p-3 rounded ${
                isEligible
                  ? 'bg-green-50 dark:bg-green-900/20'
                  : 'bg-gray-50 dark:bg-gray-800/20'
              }`}
            >
              <div className='flex items-start justify-between'>
                <div className='flex-1'>
                  <div className='flex items-center mb-1'>
                    <FontAwesomeIcon
                      icon={faInfoCircle}
                      className='ml-2 text-sm'
                      style={{ color: isEligible ? '#10b981' : 'var(--text-secondary)' }}
                    />
                    <span className='font-semibold'>
                      {discount.type === 'percentage'
                        ? `${makeNumbersFarsi(discount.value)}٪ تخفیف`
                        : `${formatPrice(discount.value)} تومان تخفیف`}
                    </span>
                  </div>
                  
                  {discount.description && (
                    <p className='text-sm mr-6' style={{ color: 'var(--text-secondary)' }}>
                      {discount.description}
                    </p>
                  )}

                  {!isEligible && discount.minOrderAmount && (
                    <p className='text-sm mr-6 mt-2 font-semibold' style={{ color: '#f59e0b' }}>
                      برای دریافت این تخفیف، حداقل{' '}
                      {formatPrice(discount.minOrderAmount)} تومان خرید کنید
                    </p>
                  )}

                  {isEligible && (
                    <p className='text-sm mr-6 mt-2 font-semibold' style={{ color: '#10b981' }}>
                      ✓ این تخفیف به صورت خودکار اعمال می‌شود
                    </p>
                  )}
                </div>
              </div>
            </div>
          )
        })}
      </div>
    </div>
  )
}

